/**
 * IndexedDB persistence for executive calendar event buckets.
 * Survives page reloads so the grid can paint before any network call.
 */

import type { EventsCacheBucket } from './executiveCalendarEventCache';

const DB_NAME = 'ea-calendar-cache-v1';
const STORE = 'buckets';
const DB_VERSION = 1;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
  });
}

export async function loadIndexedEventBucket(cacheKey: string): Promise<EventsCacheBucket | null> {
  try {
    const db = await openDb();
    const row = await new Promise<EventsCacheBucket | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(cacheKey);
      req.onsuccess = () => resolve((req.result as EventsCacheBucket) || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!row) return null;
    if (Date.now() - row.lastSyncAt > MAX_AGE_MS) return null;
    return row;
  } catch {
    return null;
  }
}

export async function saveIndexedEventBucket(bucket: EventsCacheBucket): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(bucket, bucket.cacheKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* quota / private mode */
  }
}
